import React, { useMemo, useCallback, useState, useEffect } from 'react';
import TokenSocialTab from './TokenSocialTab';
import CopyButton from './CopyButton';
import { useUIStore } from '../stores/ui/uiStore';
import type { TokenState } from '../stores/data/types';
import { TokenStatus } from '../stores/data/types';
import StatusChip from './StatusChip';
import CategoryChip from './CategoryChip';
import KeywordChip from './KeywordChip';
import KeywordEditor from './KeywordEditor';
import { feManagementSystemApi } from '../api/fe-management-system-api';
import { useTweetContentStore } from '../stores/tweetContent/tweetContentStore';
import { tweetContentApi } from '../api/bip-api/tweet-content';
import { TweetStatus } from '../api/bip-api/types';

interface TokenDetailHeaderProps {
  tokenAddress: string;
  tokenData: TokenState | null;
  onClose: () => void;
  onStartInvestigationSync?: () => void;
  /** When provided, hides the toolbar Create Note (Twitter sync) action if non-empty after trim. When omitted, falls back to feTokenState.contentsExists. */
  noteContents?: string | null;
}

const TokenDetailHeader: React.FC<TokenDetailHeaderProps> = ({
  tokenAddress,
  tokenData,
  onClose,
  onStartInvestigationSync,
  noteContents,
}) => {
  const { selectedToken, openCreateNoteModal } = useUIStore();
  const openTweetModal = useTweetContentStore((state) => state.openModal);
  const displaySymbol = tokenData?.tokenInfo?.tokenSymbol || selectedToken?.symbol || 'Unknown';
  const displayName = tokenData?.tokenInfo?.tokenName || 'Unknown Token';
  const hasOrderIssue = tokenData?.hasOrderIssue;
  const selectedStatus = tokenData?.feTokenState?.status || TokenStatus.NONE;
  const selectedCategory = tokenData?.feTokenState?.category;
  const convictionRank = tokenData?.ranking?.cumulativeConvictionAtSameTimeRank;
  const [keywords, setKeywords] = useState<string[]>([]);
  const [isKeywordAreaHovered, setIsKeywordAreaHovered] = useState(false);
  const [showKeywordEditor, setShowKeywordEditor] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [hasSocialSources, setHasSocialSources] = useState(false);

  useEffect(() => {
    const fetchTokenState = async () => {
      try {
        const tokenState = await feManagementSystemApi.getTokenState(tokenAddress);
        setKeywords(tokenState.keywords || []);
        const note = tokenState.note;
        const sources = note?.socialSources ?? note?.social_sources ?? [];
        setHasSocialSources(sources.length > 0);
      } catch (error) {
        console.error('Failed to fetch token state:', error);
        setKeywords([]);
        setHasSocialSources(false);
      }
    };

    if (tokenAddress) {
      fetchTokenState();
    }
  }, [tokenAddress]);

  useEffect(() => {
    const fetchPendingCount = async () => {
      try {
        const tweets = await tweetContentApi.getTweetContents(tokenAddress);
        const count = tweets.filter((t) => t.status === TweetStatus.PENDING).length;
        setPendingCount(count);
      } catch (error) {
        console.error('Failed to fetch pending tweet count:', error);
        setPendingCount(0);
      }
    };

    if (tokenAddress) {
      fetchPendingCount();
    }
  }, [tokenAddress]);

  const restrictedSocialActions = [
    TokenStatus.NONE,
    TokenStatus.FETCHING_DATA,
    TokenStatus.LATER,
  ];
  const canOpenSocialNote = !restrictedSocialActions.includes(selectedStatus);

  const hasExistingNoteContents =
    noteContents != null
      ? noteContents.trim().length > 0
      : Boolean(tokenData?.feTokenState?.contentsExists);

  const shortAddress = useMemo(() => {
    if (!tokenAddress) return '';
    return `${tokenAddress.slice(0, 8)}...${tokenAddress.slice(-8)}`;
  }, [tokenAddress]);

  const handleInvestigationClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      onStartInvestigationSync?.();
    },
    [onStartInvestigationSync],
  );

  const poolAddress = tokenData?.tokenInfo?.poolAddress;

  return (
    <div className="p-4 border-b border-gray-700 flex-shrink-0">
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-3">
          {tokenData?.tokenInfo?.imageUrl && (
            <div
              className="mt-2 w-8 h-8 rounded-lg border border-gray-600 flex items-center justify-center overflow-hidden bg-cover bg-center"
              style={{
                backgroundImage: `url(${tokenData.tokenInfo.imageUrl})`,
              }}
            />
          )}
          <div className="flex-1">
            <div className="flex items-center space-x-2">
              <h2 className="text-lg font-semibold text-white">{displaySymbol}</h2>
              {hasOrderIssue && (
                <div className="flex items-center" title="Order Issue가 있습니다">
                  <svg
                    className="w-5 h-5 text-yellow-400"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              )}
              <CategoryChip category={selectedCategory} className="ml-1" />
              <StatusChip 
                tokenAddress={tokenAddress}
                status={selectedStatus} 
              />
              {convictionRank !== undefined && convictionRank > 0 && (
                <div className="flex items-center px-2 py-0.5 rounded text-[12px] font-bold bg-purple-500/20 text-purple-400 border border-purple-500/30 ml-1">
                  Rank: {convictionRank}%
                </div>
              )}
            </div>
            <div className="flex items-center justify-start mb-2">
              <p className="text-sm text-gray-400">{displayName}</p>
              <div
                className="flex items-center gap-1 ml-3 mt-2 min-w-0 flex-1"
                onMouseEnter={() => setIsKeywordAreaHovered(true)}
                onMouseLeave={() => setIsKeywordAreaHovered(false)}
              >
                {showKeywordEditor ? (
                  <div className="relative">
                    <KeywordEditor
                      tokenAddress={tokenAddress}
                      initialKeywords={keywords}
                      onSuccess={async () => {
                        try {
                          const tokenState = await feManagementSystemApi.getTokenState(tokenAddress);
                          setKeywords(tokenState.keywords || []);
                        } catch (error) {
                          console.error('Failed to refetch keywords:', error);
                        }
                        setShowKeywordEditor(false);
                      }}
                      onClose={() => setShowKeywordEditor(false)}
                    />
                  </div>
                ) : (
                  <>
                    {keywords.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {keywords.map((keyword) => (
                          <KeywordChip
                            key={`${tokenAddress}-kw-${keyword}`}
                            keyword={keyword}
                          />
                        ))}
                      </div>
                    ) : (
                      <span className="text-gray-500 text-sm">-</span>
                    )}
                    {isKeywordAreaHovered && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowKeywordEditor(true);
                        }}
                        className="ml-auto px-2 py-0.5 text-[11px] rounded bg-gray-700 text-gray-200 hover:bg-gray-600 transition-colors flex-shrink-0"
                      >
                        편집
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {shortAddress && <span className="text-xs text-gray-500 font-mono">{shortAddress}</span>}
              {tokenAddress && (
                <CopyButton text={tokenAddress} title="토큰 주소 복사" />
              )}
            </div>
          </div>
        </div>

        <div className="flex align-center items-end gap-2" style={{ width: 1000, justifyContent: "space-between", alignItems: "normal" }}>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <button
                className={`w-8 h-8 rounded-sm flex items-center justify-center transition-opacity ${
                  poolAddress ? 'opacity-70 hover:opacity-100 cursor-pointer' : 'opacity-5 cursor-not-allowed'
                }`}
                onClick={() => {
                  if (poolAddress) {
                    const axiomUrl = `https://axiom.trade/meme/${poolAddress}`;
                    window.open(axiomUrl, '_blank');
                  }
                }}
                disabled={!poolAddress}
                title="Axiom"
              >
                <div className="w-8 h-8 rounded-sm overflow-hidden bg-white flex items-center justify-center">
                  <img src="/axiom.png" alt="axiom" className="w-8 h-8" />
                </div>
              </button>

              <button
                className="w-8 h-8 rounded-sm bg-white flex items-center justify-center opacity-70 hover:opacity-100 transition-opacity"
                onClick={() => {
                  const retoolUrl = `https://jsb170331.retool.com/apps/210c0d98-eec0-11ef-9e88-2b621bc60434/Solana%20Graduated%20Tokens/page5?address=${tokenAddress}`;
                  window.open(retoolUrl, '_blank');
                }}
                title="Retool"
              >
                <div className="w-8 h-8 rounded-sm overflow-hidden flex items-center justify-center">
                  <img src="/retool.svg" alt="retool" className="w-7 h-7" />
                </div>
              </button>

              <button
                className="w-8 h-8 rounded-sm flex items-center justify-center opacity-70 hover:opacity-100 transition-opacity"
                onClick={() => {
                  const link = `http://gmgn.ai/sol/token/solscan_${tokenAddress}`;
                  window.open(link, '_blank');
                }}
                title="GMGN"
              >
                <img src="/gmgn.png" alt="gmgn" className="w-8 h-8" />
              </button>

              <button
                className="w-8 h-8 rounded-sm flex items-center justify-center opacity-70 hover:opacity-100 transition-opacity"
                onClick={() => {
                  const link = `https://solscan.io/token/${tokenAddress}`;
                  window.open(link, '_blank');
                }}
                title="Solscan"
              >
                <img src="/solscan.png" alt="solscan" className="w-8 h-8" />
              </button>

              <button
                className="w-8 h-8 rounded-sm flex items-center justify-center opacity-70 hover:opacity-100 transition-opacity"
                onClick={() => {
                  const link = `https://x.com/search?q=${tokenAddress}`;
                  window.open(link, '_blank');
                }}
                title="Twitter 검색"
              >
                <svg className="w-8 h-8 text-white transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>
            </div>

            <div className="w-px h-8 border-r border-gray-600"></div>

            <div className="flex items-center space-x-2">
              <button
                className={`w-8 h-8 rounded-sm flex items-center justify-center transition-opacity ${tokenData?.tokenInfo?.twitter
                  ? 'opacity-70 hover:opacity-100 cursor-pointer'
                  : 'opacity-5 cursor-not-allowed'
                }`}
                onClick={() => {
                  if (tokenData?.tokenInfo?.twitter) {
                    window.open(tokenData.tokenInfo.twitter, '_blank');
                  }
                }}
                disabled={!tokenData?.tokenInfo?.twitter}
                title="Twitter/X"
              >
                <div className="w-8 h-8 rounded-sm overflow-hidden flex items-center justify-center">
                  <img src="/x.png" alt="x" className="w-7 h-7" />
                </div>
              </button>

              <button
                className={`w-8 h-8 rounded-sm flex items-center justify-center transition-opacity ${tokenData?.tokenInfo?.website
                  ? 'opacity-70 hover:opacity-100 cursor-pointer'
                  : 'opacity-5 cursor-not-allowed'
                }`}
                onClick={() => {
                  if (tokenData?.tokenInfo?.website) {
                    window.open(tokenData.tokenInfo.website, '_blank');
                  }
                }}
                disabled={!tokenData?.tokenInfo?.website}
                title="Website"
              >
                <div className="w-8 h-8 rounded-sm overflow-hidden flex items-center justify-center">
                  <img src="/website.png" alt="website" className="w-7 h-7" />
                </div>
              </button>

              <button
                className={`w-8 h-8 rounded-sm flex items-center justify-center transition-opacity ${tokenData?.tokenInfo?.telegram
                  ? 'opacity-90 hover:opacity-100 cursor-pointer'
                  : 'opacity-5 cursor-not-allowed'
                }`}
                onClick={() => {
                  if (tokenData?.tokenInfo?.telegram) {
                    window.open(tokenData.tokenInfo.telegram, '_blank');
                  }
                }}
                disabled={!tokenData?.tokenInfo?.telegram}
                title="Telegram"
              >
                <div className="w-8 h-8 rounded-sm overflow-hidden flex items-center justify-center">
                  <img src="/telegram.png" alt="telegram" className="w-8 h-8" />
                </div>
              </button>
            </div>

            <div className="w-px h-8 border-r border-gray-600"></div>

            <div className="flex items-center space-x-2">
              {onStartInvestigationSync && (
                <button
                  className={`w-8 h-8 rounded-sm flex items-center justify-center transition-colors ${
                    hasSocialSources
                      ? 'text-gray-400 hover:text-blue-400 cursor-pointer'
                      : 'text-gray-600 cursor-not-allowed'
                  }`}
                  onClick={handleInvestigationClick}
                  disabled={!hasSocialSources}
                  title={hasSocialSources ? 'InvestigationSync' : 'Register social sources before starting investigation sync'}
                >
                  <svg style={{ width: 20, height: 'auto' }} xmlns="http://www.w3.org/2000/svg" fill="currentColor" width="24" height="24" viewBox="0 0 24 24">
                    <path d="M12 16C12.41 16 12.81 15.97 13.21 15.94C13.4 15.18 13.72 14.46 14.16 13.83C13.47 13.94 12.74 14 12 14C9.58 14 7.3 13.4 6 12.45V9.64C7.47 10.47 9.61 11 12 11S16.53 10.47 18 9.64V11.19C18.5 11.07 19 11 19.55 11C19.7 11 19.85 11 20 11.03V7C20 4.79 16.42 3 12 3S4 4.79 4 7V17C4 19.21 7.59 21 12 21C12.66 21 13.31 20.96 13.92 20.88C13.57 20.29 13.31 19.64 13.16 18.94C12.79 19 12.41 19 12 19C8.13 19 6 17.5 6 17V14.77C7.61 15.55 9.72 16 12 16M12 5C15.87 5 18 6.5 18 7S15.87 9 12 9 6 7.5 6 7 8.13 5 12 5M23 17.5C23 18.32 22.75 19.08 22.33 19.71L21.24 18.62C21.41 18.28 21.5 17.9 21.5 17.5C21.5 16.12 20.38 15 19 15V16.5L16.75 14.25L19 12V13.5C21.21 13.5 23 15.29 23 17.5M19 18.5L21.25 20.75L19 23V21.5C16.79 21.5 15 19.71 15 17.5C15 16.68 15.25 15.92 15.67 15.29L16.76 16.38C16.59 16.72 16.5 17.1 16.5 17.5C16.5 18.88 17.62 20 19 20V18.5Z" />
                  </svg>
                </button>
              )}
              {(!canOpenSocialNote || selectedStatus === TokenStatus.FETCHING_FAILED) && !hasExistingNoteContents && (
                <button
                  className="w-8 h-8 rounded-sm flex items-center justify-center transition-colors text-gray-400 hover:text-blue-400"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (tokenData) {
                      openCreateNoteModal({
                        tokenSymbol: tokenData.tokenInfo?.tokenSymbol || 'Unknown',
                        tokenAddress: tokenData.tokenAddress,
                        tokenName: tokenData.tokenInfo?.tokenName,
                        tokenTwitter: tokenData.tokenInfo?.twitter,
                        tokenWebsite: tokenData.tokenInfo?.website,
                        tokenCategory: tokenData.feTokenState?.category,
                      });
                    }
                  }}
                  title="트위터 싱크 설정"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="w-5 h-5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
                  </svg>
                </button>
              )}
              <button
                className="w-8 h-8 rounded-sm flex items-center justify-center transition-colors text-gray-400 hover:text-blue-400 relative"
                onClick={(e) => {
                  e.stopPropagation();
                  openTweetModal(tokenAddress, displaySymbol);
                }}
                title="Manage Tweets"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="w-5 h-5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 01.865-.501 48.172 48.172 0 003.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" />
                </svg>
                {pendingCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 flex items-center justify-center text-[10px] font-bold text-white bg-red-500 rounded-full">
                    {pendingCount}
                  </span>
                )}
              </button>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <TokenSocialTab
              tokenAddress={tokenAddress}
            />
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors p-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TokenDetailHeader;
